import { ROLE_SCOPE_DEFINITIONS } from './scopes'
import type { RoleScopeName } from '../types/scope.types'

/**
 * Feature flag lifecycle, stored in `setting_definition.status` as a Postgres
 * enum. Values are lowercase; older definitions outside the featureFlags
 * namespace were never backfilled and stay null.
 */
export type FeatureFlagStatus = 'wip' | 'closed_beta' | 'open_beta' | 'ga'

export const FEATURE_FLAG_STATUS_LABELS: Record<FeatureFlagStatus, string> = {
  wip: 'WIP',
  closed_beta: 'Closed Beta',
  open_beta: 'Open Beta',
  ga: 'GA'
}

/** One-line explanation of each stage, shown in the status picker */
export const FEATURE_FLAG_STATUS_DESCRIPTIONS: Record<
  FeatureFlagStatus,
  string
> = {
  wip: 'Engineering is still building it',
  closed_beta: 'Enabled for hand-picked design partners',
  open_beta: 'Can be enabled for any interested customer',
  ga: 'On for everyone'
}

/** Lifecycle order, earliest stage first */
export const FEATURE_FLAG_STATUS_ORDER: FeatureFlagStatus[] = [
  'wip',
  'closed_beta',
  'open_beta',
  'ga'
]

/**
 * Lifecycle order, for sorting. Sorting on the raw value would order the enum
 * strings alphabetically (closed_beta, ga, open_beta, wip), which is meaningless.
 */
const FEATURE_FLAG_STATUS_RANK: Record<FeatureFlagStatus, number> = {
  wip: 0,
  closed_beta: 1,
  open_beta: 2,
  ga: 3
}

/** Sort key for the Status column. -1 keeps unset statuses before WIP. */
export const getStatusRank = (status?: FeatureFlagStatus | null): number =>
  status ? FEATURE_FLAG_STATUS_RANK[status] : -1

/** Sentinel for "don't filter by status" — a select needs a non-empty value */
export const FEATURE_FLAG_STATUS_FILTER_ALL = 'all'

interface StatusTransition {
  to: FeatureFlagStatus
  roles: RoleScopeName[]
}

/**
 * Transitions the backoffice offers, keyed by the flag's CURRENT status. Mirrors
 * FF_STATUS_TRANSITIONS in sls-backoffice-api, which enforces the same rule —
 * this copy only decides what to render. Note the backend map is keyed by the
 * TARGET status instead, so the two can't be diffed line by line.
 *
 * GA is absent on purpose: promotion to GA stays a code-level rollout by
 * engineering, so it is displayed but never offered as a transition.
 */
const FEATURE_FLAG_STATUS_TRANSITIONS: Partial<
  Record<FeatureFlagStatus, StatusTransition>
> = {
  wip: { to: 'closed_beta', roles: ['bo_engineering'] },
  closed_beta: { to: 'open_beta', roles: ['bo_engineering', 'bo_product'] }
}

const hasAnyRole = (
  userScopes: string[] | undefined,
  roles: RoleScopeName[]
): boolean => roles.some(role => (userScopes ?? []).includes(role))

/**
 * The transition a user may perform on a flag, or null if there is none —
 * either the flag has nowhere to go or the user lacks the role.
 * Admins may perform any offered transition, as elsewhere in the backoffice.
 */
export const getAllowedTransition = (
  status: FeatureFlagStatus | null | undefined,
  userScopes: string[] | undefined
): { from: FeatureFlagStatus; to: FeatureFlagStatus } | null => {
  if (!status) {
    return null
  }
  const transition = FEATURE_FLAG_STATUS_TRANSITIONS[status]
  if (!transition) {
    return null
  }
  const allowed =
    (userScopes ?? []).includes('admin') ||
    hasAnyRole(userScopes, transition.roles)
  return allowed ? { from: status, to: transition.to } : null
}

/**
 * Who may enable or disable a flag for a single organization, by the flag's current
 * status. Mirrors FF_ENABLEMENT_ROLES in sls-backoffice-api, which enforces it — this
 * copy only decides what to render.
 *
 * GA is empty on purpose: the flag is already on for everyone, so the backoffice does
 * not offer per-org overrides for it.
 */
const FEATURE_FLAG_ENABLEMENT_ROLES: Record<
  FeatureFlagStatus,
  RoleScopeName[]
> = {
  wip: ['bo_engineering'],
  closed_beta: ['bo_engineering', 'bo_product'],
  open_beta: ['bo_engineering', 'bo_product', 'bo_techgtm', 'bo_gtm'],
  ga: []
}

/** The roles that may flip a flag at this status. An unset status fails closed. */
const getEnablementRoles = (
  status: FeatureFlagStatus | null | undefined
): RoleScopeName[] => (status ? FEATURE_FLAG_ENABLEMENT_ROLES[status] : [])

const describeBlockedStatus = (
  status: FeatureFlagStatus | null | undefined
): string => {
  if (!status) {
    return 'This flag has no lifecycle status'
  }
  if (status === 'ga') {
    return 'GA flags are on for everyone and are not set per organization'
  }
  const roles = getEnablementRoles(status)
    .map(role => ROLE_SCOPE_DEFINITIONS[role].label)
    .join(', ')
  return `${FEATURE_FLAG_STATUS_LABELS[status]} flags can only be changed by ${roles}`
}

/** The statuses whose flags this user may change for an org, earliest stage first */
export const getChangeableStatuses = (
  userScopes: string[] | undefined
): FeatureFlagStatus[] => {
  return FEATURE_FLAG_STATUS_ORDER.filter(status =>
    hasAnyRole(userScopes, getEnablementRoles(status))
  )
}

/**
 * Why this flag can't be flipped for one organization, or null when it can.
 *
 * is_backoffice_visible is not part of this: it only hides a flag from the default
 * list view, so status and role are the whole rule, matching the backend.
 *
 * Deliberately ignores the admin scope: admins may still flip a blocked flag, but
 * the reason has to reach them as an override warning rather than disappear.
 */
export const getOrgToggleBlockedReason = (
  status: FeatureFlagStatus | null | undefined,
  userScopes: string[] | undefined
): string | null =>
  hasAnyRole(userScopes, getEnablementRoles(status))
    ? null
    : describeBlockedStatus(status)
